import { useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";
// State
import { useReportDownload } from "../../state/reportDownload";
// Components
import { RHFSelectField } from "../../components/RHFSelectField";
// MUI
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

export const ReportsForm = ({ type, branches, reports }) => {
  const { setAlertNotification } = useContext(AlertNotificationContext);

  const [reportFilter, setReportFilter] = useState({
    enabled: false,
    report: "",
    fileFormat: "",
    startDateFilter: "",
    endDateFilter: "",
    branchFilter: null,
    typeFilter: "",
  });

  // Form
  let defaultValues = {
    report: "",
    fileFormat: "xls",
    branchFilter: "",
    typeFilter: {
      Invoice: undefined,
      Credit: undefined,
      Order: undefined,
      Draft: undefined,
    },
    startDateFilter: dayjs().subtract(1, "month"),
    endDateFilter: dayjs(),
  };

  const convertFilterObjToString = (filterObj) => {
    let filterString = "";
    for (const [key, value] of Object.entries(filterObj)) {
      if (key === "Draft") {
        filterString += `${key}=${value}`;
      } else {
        filterString += `${key}=${value}|`;
      }
    }
    return filterString;
  };

  const validationHelper = {
    report: { required: "Report type is required" },
    fileFormat: { required: "File format is required" },
    branchFilter: { required: "Branch is required" },
    typeFilter: { required: "Type is required" },
    startDateFilter: { required: "Start Date is required" },
    endDateFilter: { required: "End Date is required" },
  };

  const { handleSubmit, reset, control, setValue, watch } = useForm({
    defaultValues,
  });
  const watchReportType = watch("report", false);
  const watchEndDate = watch("endDateFilter", false);

  //   enabled,report,fileFormat,branchFilter,typeFilter
  const { isFetching, isError, data, error, isSuccess } =
    useReportDownload(reportFilter);

  // Success Function
  const successFunc = () => {
    if (reportFilter.fileFormat === "xls") {
      validateXLS(data.data.data.fileContent, data.data.data.fileName);
    } else if (reportFilter.fileFormat === "pdf") {
      validatePDF(data.data.data.fileContent, data.data.data.fileName);
    }
    setReportFilter({
      enabled: false,
      report: "",
      fileFormat: "",
      startDateFilter: "",
      endDateFilter: "",
      branchFilter: null,
      typeFilter: "",
    });
  };
  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });
  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  const handleOnSubmit = (data) => {
    setReportFilter({
      enabled: true,
      report: data.report,
      fileFormat: data.fileFormat,
      startDateFilter: dayjs(data.startDateFilter).format("YYYY-MM-DD"),
      endDateFilter: dayjs(data.endDateFilter).format("YYYY-MM-DD"),
      branchFilter: data.branchFilter,
      typeFilter: convertFilterObjToString(data.typeFilter),
    });
  };

  const validatePDF = (base64String, docNo) => {
    if (base64String.startsWith("JVB")) {
      base64String = "data:application/pdf;base64," + base64String;
      downloadFile(base64String, `${docNo}.pdf`);
    } else if (base64String.startsWith("data:application/pdf;base64")) {
      downloadFile(base64String, `${docNo}.pdf`);
    } else {
      setAlertNotification({
        message: `Error: Not a valid Base64 PDF string`,
        severity: "warning",
      });
    }
  };

  //   "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,";
  const validateXLS = (base64String, fileName) => {
    if (base64String.startsWith("UEs")) {
      base64String =
        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
        base64String;
      downloadFile(base64String, `${fileName}`);
    } else if (
      base64String.startsWith(
        "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64",
      )
    ) {
      downloadFile(base64String, `${fileName}`);
    } else {
      setAlertNotification({
        message: `Error: Not a valid Base64 XLS string`,
        severity: "warning",
      });
    }
  };

  const downloadFile = (file, fileName) => {
    const link = document.createElement("a");
    link.download = fileName;
    link.href = file;
    link.click();
  };

  const handleReset = () => {
    reset();
  };

  const reportIndex = (reportType, reports) =>
    reports.findIndex((item) => item.id === reportType);

  const ReportType = () => (
    <RHFSelectField
      name="report"
      label="Report"
      options={reports}
      optionIdentifiers={{ id: "id", label: "label" }}
      control={control}
      validationHelper={validationHelper}
      rules={{ required: true }}
      viewMode={false}
    />
  );

  const FileFormat = () => (
    <RHFSelectField
      name="fileFormat"
      label="File Format"
      options={reports[reportIndex(watchReportType, reports)].fileFormats}
      optionIdentifiers={{ id: "id", label: "label" }}
      control={control}
      validationHelper={validationHelper}
      rules={{ required: true }}
      viewMode={false}
    />
  );

  const BranchFilter = () => (
    <RHFSelectField
      name="branchFilter"
      label="Branch"
      options={branches}
      optionIdentifiers={{ id: "no", label: "name" }}
      control={control}
      validationHelper={validationHelper}
      rules={{ required: false }}
      viewMode={false}
    />
  );

  const TypeFilterInvoice = () => (
    <Controller
      name="typeFilter.Invoice"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControlLabel
          control={<Switch checked={!!value} onChange={onChange} />}
          label="Invoices"
        />
      )}
    />
  );

  const TypeFilterOrders = () => (
    <Controller
      name="typeFilter.Order"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControlLabel
          control={<Switch checked={!!value} onChange={onChange} />}
          label="Orders"
        />
      )}
    />
  );

  const TypeFilterDrafts = () => (
    <Controller
      name="typeFilter.Draft"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControlLabel
          control={<Switch checked={!!value} onChange={onChange} />}
          label="Drafts"
        />
      )}
    />
  );

  const TypeFilterCredits = () => (
    <Controller
      name="typeFilter.Credit"
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormControlLabel
          control={<Switch checked={!!value} onChange={onChange} />}
          label="Credits"
        />
      )}
    />
  );

  const StartDate = () => (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-gb"}>
      <Controller
        name="startDateFilter"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, value } }) => (
          <DatePicker
            minDate={dayjs(watchEndDate).subtract(1, "year")}
            maxDate={watchEndDate}
            label="Start Date"
            value={value}
            onChange={onChange}
            renderInput={(params) => <TextField {...params} />}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );

  const EndDate = () => (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-gb"}>
      <Controller
        name="endDateFilter"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, value } }) => (
          <DatePicker
            disableFuture
            label="End Date"
            value={value}
            onChange={onChange}
            renderInput={(params) => <TextField {...params} />}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        maxWidth: 600,
      }}
      component="form"
      autoComplete="off"
    >
      <Card>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ m: 2 }}>
            {type === "general" ? "General Reports" : "Financial Reports"}
          </Typography>
        </Box>
        <Divider />
        <Stack direction="column" spacing={3} sx={{ m: 2 }}>
          <ReportType />
          {watchReportType && (
            <>
              {reports[reportIndex(watchReportType, reports)].branchFilter && (
                <BranchFilter />
              )}
              {reports[reportIndex(watchReportType, reports)].typeFilter && (
                <Stack
                  direction="row"
                  spacing={2}
                  useFlexGap
                  sx={{ flexWrap: "wrap" }}
                >
                  <TypeFilterInvoice />
                  <TypeFilterOrders />
                  <TypeFilterDrafts />
                  <TypeFilterCredits />
                </Stack>
              )}
              {reports[reportIndex(watchReportType, reports)].dateFilter && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <StartDate />
                  <EndDate />
                </Stack>
              )}
              <FileFormat />
            </>
          )}
        </Stack>
        <Divider />
        <CardActions
          sx={{
            display: "flex",
            alignSelf: "stretch",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            p: 2,
          }}
        >
          <Button onClick={handleReset} variant="outlined">
            Reset
          </Button>
          <Button
            onClick={handleSubmit(handleOnSubmit)}
            variant="contained"
            disabled={isFetching}
            endIcon={!isFetching && <FileDownloadIcon />}
          >
            {isFetching ? "Downloading.." : "Download"}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};
